import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Utility function to generate a packing slip PDF
export const generatePackingSlipPDF = async ({
  deliveryData, // Array of delivery objects
  jobsData, // Array of job or kanban_job data (Job or KanbanJob data)
  poNumber, // Purchase Order number
  newDelivery, // Object containing ship_by, tracking_number, notes
  isKanban = false, // Flag to determine if this is a Kanban job
  usePartNumberMaster = true, // Flag to determine whether to use part_number_master
}) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const primaryColor = '#384f77';
  const white = '#ffffff';

  // Header
  doc.setFillColor(white);
  doc.rect(0, 0, pageWidth, 20, 'F');
  doc.setFontSize(24);
  doc.setTextColor(primaryColor);
  doc.text('PACKING SLIP', pageWidth / 2, 14, { align: 'center' });

  // Company Information
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(12);
  doc.setTextColor(primaryColor);
  doc.setFont('helvetica', 'bold');
  doc.text('Advanced Controls & Automation', 15, 30);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text('574 Hudson River Road', 15, 36);
  doc.text('Waterford', 15, 42);
  doc.text('NY 12188', 15, 48);

  // Add Company Logo
  const logoImg = new Image();
  logoImg.src = require('../assets/acaLogo.png');
  try {
    await new Promise((resolve, reject) => {
      logoImg.onload = resolve;
      logoImg.onerror = () => reject(new Error('Logo image failed to load'));
      setTimeout(() => reject(new Error('Logo image load timeout')), 5000);
    });
    doc.addImage(logoImg, 'PNG', pageWidth - 45, 25, 30, 30);
  } catch (err) {
    console.error('Error loading logo:', err);
    doc.text('Logo Missing', pageWidth - 45, 40);
  }

  // Package, Ship By, Ship Date, Tracking, and PO Information
  const packageNumber = deliveryData[0]?.package_number || `PKG${Date.now()}`;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  };
  
  const shipDate = formatDate(deliveryData[0]?.ship_date);  doc.setFontSize(10);

  doc.setTextColor(primaryColor);
  doc.text(`Package #: ${packageNumber}`, 15, 60);
  doc.text(`Ship By: ${newDelivery.ship_by || 'N/A'}`, 15, 66);
  doc.text(`Ship Date: ${shipDate}`, 15, 72);
  let nextY = 78;
  if (newDelivery.tracking_number) {
    doc.text(`Tracking #: ${newDelivery.tracking_number}`, 15, 78);
    nextY = 84;
  }
  doc.text(`Purchase Order #: ${poNumber}`, pageWidth - 15, nextY, { align: 'right' });

  // Customer Information
  doc.setFontSize(12);
  doc.setTextColor(primaryColor);
  doc.setFont('helvetica', 'bold');
  let billTo = { name: 'N/A', address: 'N/A', city: 'N/A', state: 'N/A', zip: 'N/A', contact: 'N/A' };
  let shipTo = { name: 'N/A', address: 'N/A', city: 'N/A', state: 'N/A', zip: 'N/A', contact: 'N/A' };
  if (jobsData.length > 0 && jobsData[0].customers) {
    const customer = jobsData[0].customers;
    billTo = {
      name: customer.name || 'N/A',
      address: customer.billing_address || 'N/A',
      city: customer.billing_city || 'N/A',
      state: customer.billing_state || 'N/A',
      zip: customer.billing_zip || 'N/A',
      contact: customer.billing_contact_name || customer.billing_contact_email || customer.billing_contact_phone || 'N/A',
    };
    shipTo = {
      name: customer.name || 'N/A',
      address: customer.shipping_address || 'N/A',
      city: customer.shipping_city || 'N/A',
      state: customer.shipping_state || 'N/A',
      zip: customer.shipping_zip || 'N/A',
      contact: customer.shipping_contact_name || customer.shipping_contact_email || customer.shipping_contact_phone || 'N/A',
    };
  }

  // Bill To
  doc.text('Bill To:', 15, 96);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  let billToY = 102;
  doc.text(billTo.name, 15, billToY);
  if (billTo.address !== 'N/A') {
    billToY += 6;
    doc.text(billTo.address, 15, billToY);
  }
  if (billTo.city !== 'N/A' && billTo.state !== 'N/A' && billTo.zip !== 'N/A') {
    billToY += 6;
    doc.text(`${billTo.city}, ${billTo.state} ${billTo.zip}`, 15, billToY);
  } else {
    const cityStateZip = [
      billTo.city !== 'N/A' ? billTo.city : '',
      billTo.state !== 'N/A' ? billTo.state : '',
      billTo.zip !== 'N/A' ? billTo.zip : '',
    ].filter(Boolean).join(', ');
    if (cityStateZip) {
      billToY += 6;
      doc.text(cityStateZip, 15, billToY);
    }
  }
  if (billTo.contact !== 'N/A') {
    billToY += 6;
    doc.text(`Contact: ${billTo.contact}`, 15, billToY);
  }

  // Ship To
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(primaryColor);
  doc.text('Ship To:', pageWidth / 2 + 5, 96);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  let shipToY = 102;
  doc.text(shipTo.name, pageWidth / 2 + 5, shipToY);
  if (shipTo.address !== 'N/A') {
    shipToY += 6;
    doc.text(shipTo.address, pageWidth / 2 + 5, shipToY);
  }
  if (shipTo.city !== 'N/A' && shipTo.state !== 'N/A' && shipTo.zip !== 'N/A') {
    shipToY += 6;
    doc.text(`${shipTo.city}, ${shipTo.state} ${shipTo.zip}`, pageWidth / 2 + 5, shipToY);
  } else {
    const cityStateZip = [
      shipTo.city !== 'N/A' ? shipTo.city : '',
      shipTo.state !== 'N/A' ? shipTo.state : '',
      shipTo.zip !== 'N/A' ? shipTo.zip : '',
    ].filter(Boolean).join(', ');
    if (cityStateZip) {
      shipToY += 6;
      doc.text(cityStateZip, pageWidth / 2 + 5, shipToY);
    }
  }
  if (shipTo.contact !== 'N/A') {
    shipToY += 6;
    doc.text(`Contact: ${shipTo.contact}`, pageWidth / 2 + 5, shipToY);
  }

  // Delivery Items Table
  doc.setFontSize(10);
  const tableData = deliveryData.map((delivery) => {
    const job = jobsData.find(j => 
      (isKanban ? j.id === delivery.kanban_job_id : j.id === delivery.job_id)
    );
  
    let partNumber;
    if (usePartNumberMaster) {
      partNumber = job?.parts?.part_number_master || 'N/A';
      if (!job?.parts?.part_number_master && job?.part_number) {
        console.warn(`Missing part_number_master, falling back to part_number: ${job.part_number}`);
        partNumber = job.part_number;
      }
      console.log("job:", job);
      console.log("Using part number:", partNumber);
    } else {
      partNumber = job?.part_number || 'N/A';
    }
  
    return [
      partNumber,
      job?.description || 'N/A',
      `Line ${delivery.po_line_number}`,
      delivery.quantity,
    ];
  });
  
  

  autoTable(doc, {
    startY: Math.max(billToY, shipToY) + 10,
    head: [['PART NUMBER', 'DESCRIPTION', 'PO LINE #', 'QTY']],
    body: tableData,
    theme: 'grid',
    styles: { fontSize: 10, cellPadding: 2, textColor: [0, 0, 0] },
    headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    margin: { left: 15, right: 15 },
    columnStyles: {
      0: { cellWidth: 40 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 40 },
      3: { cellWidth: 20 },
    },
  });

  // Total Quantity
  const totalQty = deliveryData.reduce((sum, delivery) => sum + delivery.quantity, 0);
  doc.setFontSize(12);
  doc.setTextColor(primaryColor);
  doc.setFont('helvetica', 'bold');
  doc.text(`Total: ${totalQty}`, pageWidth - 15, doc.lastAutoTable.finalY + 10, { align: 'right' });

  // Notes
  let lastY = doc.lastAutoTable.finalY + 20;
  if (newDelivery.notes) {
    doc.setFontSize(10);
    doc.setTextColor(primaryColor);
    doc.setFont('helvetica', 'bold');
    doc.text('Notes:', 15, lastY);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.text(newDelivery.notes, 15, lastY + 6, { maxWidth: pageWidth - 30 });
    lastY += 20;
  }

  // Made in America Image
  const madeInAmericaImg = new Image();
  madeInAmericaImg.src = require('../assets/madeInAmerica.png');
  try {
    await new Promise((resolve, reject) => {
      madeInAmericaImg.onload = resolve;
      madeInAmericaImg.onerror = () => reject(new Error('Made in America image failed to load'));
      setTimeout(() => reject(new Error('Made in America image load timeout')), 5000);
    });
    doc.addImage(madeInAmericaImg, 'PNG', pageWidth - 67.5, pageHeight - 25, 52.5, 15);
  } catch (err) {
    console.error('Error loading Made in America image:', err);
    doc.text('Made in America Missing', pageWidth - 67.5, pageHeight - 25);
  }

  return doc.output('blob');
};